/**
 * API Client for Ophth Consent Platform
 * Handles all communication with the backend
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

class ApiError extends Error {
  constructor(message, status, code) {
    super(message);
    this.status = status;
    this.code = code;
    this.name = 'ApiError';
  }
}

// Token management - Updated to use Supabase token
let authToken = localStorage.getItem('supabase_token') || localStorage.getItem('authToken');

export function setAuthToken(token) {
  authToken = token;
  if (token) {
    localStorage.setItem('supabase_token', token);
    // Also set old key for backward compatibility
    localStorage.setItem('authToken', token);
  } else {
    localStorage.removeItem('supabase_token');
    localStorage.removeItem('authToken');
  }
}

export function getAuthToken() {
  // Always prefer Supabase token
  return localStorage.getItem('supabase_token') || authToken;
}

export function isAuthenticated() {
  return !!(localStorage.getItem('supabase_token') || authToken);
}

// Base fetch wrapper
async function apiFetch(endpoint, options = {}) {
  const url = `${API_URL}${endpoint}`;

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Always get the latest token from localStorage
  const currentToken = getAuthToken();
  if (currentToken) {
    headers['Authorization'] = `Bearer ${currentToken}`;
  }

  // Add clinic ID header if available
  const currentClinicId = localStorage.getItem('currentClinicId');
  if (currentClinicId) {
    headers['X-Clinic-Id'] = currentClinicId;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiError(
      data.error || 'Request failed',
      response.status,
      data.code
    );
  }

  return data;
}

// =============================================================================
// Auth API
// =============================================================================

export const auth = {
  async login(email, password) {
    const data = await apiFetch('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setAuthToken(data.token);
    return data;
  },
  
  async register(userData) {
    const data = await apiFetch('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    setAuthToken(data.token);
    return data;
  },
  
  async getProfile() {
    return apiFetch('/api/auth/me');
  },
  
  async updateProfile(updates) {
    return apiFetch('/api/auth/me', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },
  
  async getClinics() {
    return apiFetch('/api/auth/clinics');
  },
  
  logout() {
    setAuthToken(null);
  },
};

// =============================================================================
// Clinics API
// =============================================================================

export const clinics = {
  async get(clinicId) {
    return apiFetch(`/api/clinics/${clinicId}`);
  },
  
  async update(clinicId, updates) {
    return apiFetch(`/api/clinics/${clinicId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },
  
  async getTemplates(clinicId) {
    return apiFetch(`/api/clinics/${clinicId}/templates`);
  },
  
  async createTemplate(clinicId, template) {
    return apiFetch(`/api/clinics/${clinicId}/templates`, {
      method: 'POST',
      body: JSON.stringify(template),
    });
  },
  
  async updateTemplate(clinicId, templateId, template) {
    return apiFetch(`/api/clinics/${clinicId}/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(template),
    });
  },
  
  async deleteTemplate(clinicId, templateId) {
    return apiFetch(`/api/clinics/${clinicId}/templates/${templateId}`, {
      method: 'DELETE',
    });
  },

  async resetTemplates(clinicId) {
    return apiFetch(`/api/clinics/${clinicId}/templates/reset`, {
      method: 'POST',
    });
  },
};

// =============================================================================
// Consents API
// =============================================================================

export const consents = {
  async list(params = {}) {
    const query = new URLSearchParams(params).toString();
    const response = await apiFetch(`/api/consents${query ? `?${query}` : ''}`);
    // Backend returns { data: [...], pagination: {...} }
    return response.data || response;
  },
  
  async get(consentId) {
    return apiFetch(`/api/consents/${consentId}`);
  },
  
  async create(consentData) {
    return apiFetch('/api/consents', {
      method: 'POST',
      body: JSON.stringify(consentData),
    });
  },
  
  async finalize(consentId) {
    return apiFetch(`/api/consents/${consentId}/finalize`, {
      method: 'POST',
    });
  },
  
  async startDoctorSigning(consentId) {
    return apiFetch(`/api/consents/${consentId}/sign/doctor/start`, {
      method: 'POST',
    });
  },
  
  // Download PDF (returns blob URL)
  async downloadPDF(consentId) {
    const token = localStorage.getItem('supabase_token') || localStorage.getItem('authToken');
    const clinicId = localStorage.getItem('currentClinicId');
    const headers = { 'Authorization': `Bearer ${token}` };
    if (clinicId) headers['X-Clinic-Id'] = clinicId;
    const response = await fetch(`${API_URL}/api/consents/${consentId}/pdf`, { headers });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Download failed' }));
      throw new Error(error.error || 'Failed to download PDF');
    }
    
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },
  
  // Download Word document (returns blob URL)
  async downloadWord(consentId) {
    const token = localStorage.getItem('supabase_token') || localStorage.getItem('authToken');
    const clinicId = localStorage.getItem('currentClinicId');
    const headers = { 'Authorization': `Bearer ${token}` };
    if (clinicId) headers['X-Clinic-Id'] = clinicId;
    const response = await fetch(`${API_URL}/api/consents/${consentId}/word`, { headers });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Download failed' }));
      throw new Error(error.error || 'Failed to download Word document');
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },

  // Public endpoints (no auth needed)
  async getForPatientSigning(token) {
    return apiFetch(`/api/consents/sign/${token}`);
  },
  
  async submitPatientSignature(token, signatureData) {
    return apiFetch(`/api/consents/sign/${token}`, {
      method: 'POST',
      body: JSON.stringify(signatureData),
    });
  },
};

// =============================================================================
// Patients API
// =============================================================================

export const patients = {
  async list(params = {}) {
    const query = new URLSearchParams(params).toString();
    return apiFetch(`/api/patients${query ? `?${query}` : ''}`);
  },

  async get(patientId) {
    return apiFetch(`/api/patients/${patientId}`);
  },

  async create(patientData) {
    return apiFetch('/api/patients', {
      method: 'POST',
      body: JSON.stringify(patientData),
    });
  },

  async update(patientId, data) {
    return apiFetch(`/api/patients/${patientId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async delete(patientId) {
    return apiFetch(`/api/patients/${patientId}`, {
      method: 'DELETE',
    });
  },

  async search(query) {
    return apiFetch(`/api/patients/search?q=${encodeURIComponent(query)}`);
  },
};

// =============================================================================
// Health Check
// =============================================================================

export async function healthCheck() {
  return apiFetch('/health');
}

// Default export
export default {
  auth,
  clinics,
  consents,
  patients,
  healthCheck,
  setAuthToken,
  getAuthToken,
  isAuthenticated,
  ApiError,
};
